import * as React from 'react'
import { ApolloConsumer, Query } from 'react-apollo'
import uuid from 'uuid/v1'

import Loader from 'main/components/common/Loader'
import { AppContext } from 'main/framework'
import { GatheringFields } from 'main/gql/gathering/'
import { GatheringRegistrations_gatheringRegistrations } from 'main/gql/registration/'
import { User, USER_QUERY } from 'main/gql/user'
import { Modal } from 'main/ui'
import { login } from 'main/util/auth'
import { UnreachableCaseError } from 'main/util/ts'
import Content from './Content'
import { track } from './track'
import { Step, StepName } from './types'

interface IProps {
  registration?: GatheringRegistrations_gatheringRegistrations | null
  gathering: GatheringFields
  schoolId: number
  onComplete?: () => void
  onRequestClose: () => void
}

export default function GatheringRegistrationModal(props: IProps) {
  const { gathering, registration, schoolId, onComplete, onRequestClose } = props

  const [step, setStep] = React.useState<Step | null>(null)
  const appContext = React.useContext(AppContext)

  const [sessionId] = React.useState(uuid())
  const trackData = { schoolId, sessionId }
  React.useEffect(() => track('opened', trackData), [])

  return (
    <ApolloConsumer>
      {client => (
        <Query<User> query={USER_QUERY}>
          {({ data, loading, error }) => {
            if (loading || error || !data) {
              return <LoadingModal handleRequestClose={handleRequestClose} />
            }

            const { user } = data

            if (!user && !step) setStep({ __stepname: StepName.SignUp })
            else if (user && !step) {
              registration
                ? setStep({ __stepname: StepName.Update, registration, user })
                : setStep({ __stepname: StepName.Register, gathering, user })
            } else if (user && step && step.__stepname === StepName.Register && registration) {
              setStep({ __stepname: StepName.Update, registration, user })
            }

            return (
              <Modal title={step ? title(step) : ''} onRequestClose={handleRequestClose}>
                <Content
                  gathering={gathering}
                  schoolId={schoolId}
                  step={step}
                  trackData={trackData}
                  onAuth={credentials => {
                    login(client, credentials, appContext)
                  }}
                  onChangeStep={setStep}
                  onComplete={onComplete}
                  onRequestClose={handleRequestClose}
                />
              </Modal>
            )
          }}
        </Query>
      )}
    </ApolloConsumer>
  )

  function handleRequestClose() {
    track('closed', trackData)
    onRequestClose()
  }
}

function LoadingModal(props: { handleRequestClose: () => void }) {
  const { handleRequestClose } = props

  return (
    <Modal title="" onRequestClose={handleRequestClose}>
      <Loader />
    </Modal>
  )
}

function title(step: Step) {
  switch (step.__stepname) {
    case StepName.Login:
      return 'Login'
    case StepName.SignUp:
      return 'RSVP to this event'
    case StepName.Register:
      return `Welcome, ${step.user.firstName}!`
    case StepName.Update:
      return `Update your RSVP!`
    case StepName.Confirmation:
      const ticket = step.registration.quantity > 1 ? 'tickets' : 'ticket'
      return `Success! You have ${step.registration.quantity} ${ticket} to ${step.gathering.name}.`
    default:
      throw new UnreachableCaseError(step)
  }
}
